import { useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import {
  Link,
  // useSearchParams
} from "react-router";
import { RoomType } from "@prisma/client";
import Star from "@app/icons/Star";
import ProfileDropDown from "./ProfileDropDown";
import useUser from "@app/zustand/user";
import getRoomName from "@app/utils/get-room-name";
import {
  BellAlertIcon as BellAlertIconOutline,
  BellIcon as BellIconOutline,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconOutline,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  BellAlertIcon as BellAlertIconSolid,
  BellIcon as BellIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
} from "@heroicons/react/20/solid";
import useMedspotStore from "@app/zustand/store";
import { refreshUser } from "@app/utils/use-most-fresh-user";

export default function Drawer() {
  const me = useUser((state) => state.user!);
  const isDrawerOpen = useMedspotStore((state) => state.drawerOpen);
  const setIsDrawerOpen = useMedspotStore((state) => state.setDrawerOpen);
  // const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState<"messages" | "notifications">(
    "messages",
  );

  useEffect(() => {
    refreshUser("drawer open");
  }, [isDrawerOpen]);

  return (
    <>
      <button
        onClick={() => setIsDrawerOpen(true)}
        className="z-50 flex h-full items-center justify-center"
      >
        <ChevronLeftIcon className="size-6" />
      </button>
      <aside
        className={[
          "fixed -left-full top-0 h-full w-full overflow-hidden bg-stone-100",
          // "transform transition-transform duration-300 ease-in-out",
          // isDrawerOpen ? "-translate-x-full" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full w-full flex-col overflow-hidden px-2">
          <div className="py-1 shadow-md">
            <div className="flex items-center justify-between">
              <h1 className="text-base font-bold text-sky-800">
                <Link to="/" onClick={() => setIsDrawerOpen(false)}>
                  MedSpot.fr 🩺
                  <br />
                  {me.etablissement?.includes("CHU")
                    ? "CHU & périph"
                    : me.etablissement}
                </Link>
              </h1>
              <ProfileDropDown />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pb-40">
            {currentTab === "messages" && <Messages />}
            {currentTab === "notifications" && <Notifications />}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex h-12 items-center justify-evenly bg-white">
          <button type="button" onClick={() => setCurrentTab("messages")}>
            {currentTab === "messages" ? (
              <ChatBubbleLeftRightIconSolid className="size-6" />
            ) : (
              <ChatBubbleLeftRightIconOutline className="size-6" />
            )}
          </button>
          <button type="button" onClick={() => setCurrentTab("notifications")}>
            {currentTab === "notifications" ? (
              me?.notificationLogs?.length > 0 ? (
                <div className="relative">
                  <BellAlertIconSolid className="size-6" />
                  <span className="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-red-500 text-xs text-white">
                    {me.notificationLogs.length}
                  </span>
                </div>
              ) : (
                <BellIconSolid className="size-6" />
              )
            ) : me?.notificationLogs?.length > 0 ? (
              <div className="relative">
                <BellAlertIconOutline className="size-6" />
                <span className="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-red-500 text-xs text-white">
                  {me.notificationLogs.length}
                </span>
              </div>
            ) : (
              <BellIconOutline className="size-6" />
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

function Messages() {
  const me = useUser((state) => state.user!);
  const setIsDrawerOpen = useMedspotStore((state) => state.setDrawerOpen);
  // const [searchParams] = useSearchParams();

  const channels = useMemo(() => {
    return me.rooms.filter((r) => r.room.roomType !== RoomType.DIRECT);
  }, [me.rooms]);

  const directMessages = useMemo(() => {
    return me.rooms
      .filter((r) => r.room.roomType === RoomType.DIRECT)
      .sort((a, b) => {
        return a.name && b.name ? a.name.localeCompare(b.name) : -1;
      });
  }, [me.rooms]);

  return (
    <>
      <div className="rounded bg-white">
        <p className="border-b border-b-gray-200 px-2 pb-2 pt-2 text-left text-xs font-semibold text-black">
          Mes fils d'actualité
        </p>
        {channels?.map((roomRelation) => {
          const room = roomRelation.room;
          if (room.roomType === RoomType.SPECIALITE) return null;
          if (room.roomType === RoomType.STATUS_PRO) return null;
          if (room.roomType === RoomType.UNIVERSITAIRE) return null;
          // const isCurrentRoom = searchParams.get("qRoomId") === room.id;
          const newMessages = room.lastMessageAt! > roomRelation.lastVisitAt!;
          return (
            <Link
              to={`/?qRoomType=${room.roomType}&qRoomId=${room.id}`}
              key={room.id}
              onClick={() => setIsDrawerOpen(false)}
              className={[
                "group flex w-full items-center justify-start border-b border-b-gray-200 px-4 py-2 text-left text-sm text-gray-900 hover:bg-sky-900 hover:text-white",
                // isCurrentRoom ? "bg-sky-900 text-white" : "",
                newMessages ? "font-semibold" : "",
              ].join(" ")}
            >
              <span className="mr-4 inline-flex shrink-0 items-center justify-center text-left">
                {room.roomType === RoomType.ETABLISSEMENT && "🏥"}
                {/* {room.roomType === RoomType.SPECIALITE && "🧰"} */}
                {/* {room.roomType === RoomType.STATUS_PRO && "🥼"} */}
                {room.roomType === RoomType.SPECIALITE_AND_STATUS_PRO && "⚕️"}
                {/* {room.roomType === RoomType.UNIVERSITAIRE && "🎓"} */}
              </span>
              <span>{getRoomName(roomRelation)}</span>
              {newMessages && (
                <span className="ml-auto size-2 shrink-0 rounded-full bg-sky-900 text-xs" />
              )}
              <ChevronRightIcon
                className={[
                  "size-4 shrink-0",
                  newMessages ? "ml-2" : "ml-auto",
                ].join(" ")}
              />
            </Link>
          );
        })}
      </div>

      <div className="mt-4 overflow-hidden rounded bg-white">
        <Link
          to={`/?qFavs=true`}
          onClick={() => setIsDrawerOpen(false)}
          className={[
            "group flex w-full items-center justify-start border-b border-b-gray-200 px-4 py-2 text-left text-sm text-gray-900 hover:bg-sky-900 hover:text-white",
            // searchParams.get("qFavs") === "true" ? "bg-sky-900 text-white" : "",
          ].join(" ")}
        >
          <span className="mr-2 inline-flex size-3 shrink-0 items-center justify-center text-left">
            <Star filled className="pointer-events-none size-3" />
          </span>
          <span>Mes posts favoris</span>
          <ChevronRightIcon className={["ml-auto size-4 shrink-0"].join(" ")} />
        </Link>
        <Link
          to={`/?search=`}
          onClick={() => setIsDrawerOpen(false)}
          className={[
            "group flex w-full items-center justify-start border-b border-b-gray-200 px-4 py-2 text-left text-sm text-gray-900 hover:bg-sky-900 hover:text-white",
            // searchParams.has("search") ? "bg-sky-900 text-white" : "",
          ].join(" ")}
        >
          <span className="mr-2 inline-flex size-3 shrink-0 items-center justify-center text-left">
            🔎
          </span>
          <span>Rechercher un post</span>
          <ChevronRightIcon className={["ml-auto size-4 shrink-0"].join(" ")} />
        </Link>
      </div>
      <div className="mt-4 overflow-hidden rounded bg-white">
        <div className="items-left flex flex-col justify-between border-b border-b-gray-200">
          <p className="px-2 pb-2 pt-2 text-left text-xs font-semibold text-black">
            Mes groupes de discussion
          </p>
          {directMessages?.length > 0 && (
            <Link
              to={{
                pathname: "/users",
                search: `qRoomType=${RoomType.DIRECT}`,
              }}
              onClick={() => setIsDrawerOpen(false)}
              className="mb-4 px-4 text-xs font-semibold text-sky-900"
            >
              + Démarrer une discussion
            </Link>
          )}
        </div>
        {directMessages?.length > 0 ? (
          directMessages?.map((roomRelation) => {
            const room = roomRelation.room;
            // const isCurrentRoom = searchParams.get("qRoomId") === room.id;
            const newMessages = room.lastMessageAt! > roomRelation.lastVisitAt!;
            return (
              <Link
                to={`/?qRoomType=${room.roomType}&qRoomId=${room.id}`}
                key={room.id}
                onClick={() => setIsDrawerOpen(false)}
                className={[
                  "group flex w-full items-center justify-start border-b border-b-gray-200 px-4 py-2 text-left text-sm text-gray-900 hover:bg-sky-900 hover:text-white",
                  // isCurrentRoom ? "bg-sky-900 text-white" : "",
                  newMessages ? "font-semibold" : "",
                ].join(" ")}
              >
                {roomRelation?.avatar ? (
                  <span className="mr-4 flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-sky-900 bg-white text-sky-900">
                    <img
                      className="h-full w-full object-cover"
                      src={roomRelation.avatar}
                      loading="lazy"
                      alt={`Avatar de ${roomRelation.name}`}
                    />
                  </span>
                ) : (
                  <span className="mr-4 inline-flex size-6 shrink-0 items-center justify-center text-left">
                    👥
                  </span>
                )}
                <span>{getRoomName(roomRelation)}</span>
                {newMessages && (
                  <span className="ml-auto size-2 shrink-0 rounded-full bg-sky-900 text-xs" />
                )}
                <ChevronRightIcon
                  className={[
                    "size-4 shrink-0",
                    newMessages ? "ml-2" : "ml-auto",
                  ].join(" ")}
                />
              </Link>
            );
          })
        ) : (
          <Link
            to={{ pathname: "/users", search: "" }}
            onClick={() => setIsDrawerOpen(false)}
            className="mr-2 flex w-full items-center justify-center rounded-full p-2 text-xs font-semibold text-sky-900"
          >
            Nouvelle discussion
          </Link>
        )}
      </div>
    </>
  );
}

function Notifications() {
  const me = useUser((state) => state.user!);
  const setIsDrawerOpen = useMedspotStore((state) => state.setDrawerOpen);
  const setUser = useUser((state) => state.setUser);
  // const [searchParams] = useSearchParams();

  if (!me.notificationLogs.length) {
    return (
      <p className="mx-auto mt-[20%] max-w-prose py-4 text-center text-sky-900">
        Vous n'avez pas de notifications
      </p>
    );
  }

  return (
    <>
      <button
        onClick={() => {
          fetch(
            `${import.meta.env.VITE_API_URL}/notification/mark-all-as-read`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
            },
          )
            .then((res) => res.json())
            .then((res) => {
              if (res.ok) {
                setUser(res.data.user);
              }
            });
        }}
        className="mx-auto mt-2 flex w-max cursor-pointer flex-row items-center gap-3 rounded-md border border-sky-900 bg-white/60 px-2 py-1 text-sm text-sky-900"
        type="button"
      >
        Marquer toutes les notifications comme lues
      </button>
      {me.notificationLogs.map((notification) => {
        return (
          <Link
            to={notification.url}
            key={notification.id}
            onClick={() => {
              setIsDrawerOpen(false);
              fetch(
                `${import.meta.env.VITE_API_URL}/notification/read/${notification.id}`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                },
              );
            }}
            className={[
              "group flex w-full items-center justify-start border-b border-b-gray-200 px-4 py-2 text-left text-sm text-gray-900 hover:bg-sky-900 hover:text-white",
            ].join(" ")}
          >
            <span>{notification.title}</span>
            <ChevronRightIcon
              className={["ml-auto size-4 shrink-0"].join(" ")}
            />
          </Link>
        );
      })}
    </>
  );
}
